import dayjs from 'dayjs';
import React from 'react';
import { Page } from 'react-facebook';
import { useQuery } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import Slider from 'react-slick';
import {
  Button, Loading, Typography
} from 'tfc-components';

import BlogCarousel from './BlogSlider';
import NotFoundBlog from './NotFoundBlog';
import {
  getFeaturedVideoAndOthers,
  getYouTubeThumbnail, mapNewsResponseToCards, NewsCardItem,
  useBlogSections
} from './helper';

import background from 'assets/images/home/background_banner_2.png';
import Icon from 'components/atoms/Icon';
import NewsCard, { ITarget } from 'components/molecules/NewsCard';
import NativePlayer from 'components/organisms/NativePlayer';
import Section from 'components/organisms/Section';
import { getNewsBlogService, getPagesBlogPreviewService, getPagesBlogService } from 'services/news';

const sortByDateDesc = (
  a: NewsCardItem,
  b: NewsCardItem
) => dayjs(b.dateTime).unix() - dayjs(a.dateTime).unix();

const sortByDisplayOrderAndDate = (
  a: NewsCardItem,
  b: NewsCardItem
) => a.displayOrder - b.displayOrder || sortByDateDesc(a, b);

const getFeaturedAndOthers = (data: any[] = []) => {
  const items = mapNewsResponseToCards(data);
  if (!items.length) return { featured: null, others: [] };

  const pinned = items
    .filter((i) => i.displayOrder === 1)
    .sort(sortByDateDesc);

  const featured = pinned[0] ?? items.sort(sortByDateDesc)[0];
  const others = items
    .filter((i) => i !== featured)
    .sort(sortByDisplayOrderAndDate);

  return { featured, others };
};

const Blog: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const sliderRef = React.useRef<Slider>(null);
  const [searchParams] = useSearchParams();
  const previewCode = searchParams.get('preview');
  const isPreview = Boolean(previewCode);
  const { data: blogPageData, isError } = useQuery({
    queryKey: ['blogPageData'],
    queryFn: () => {
      if (isPreview) {
        return getPagesBlogPreviewService({
          previewCode: previewCode!,
        });
      }
      return getPagesBlogService({ slug: slug || '', });
    },
    enabled: !!slug && !isPreview,
  });
  const {
    featured: featuredVideo,
    others: sliderVideos
  } = getFeaturedVideoAndOthers(blogPageData?.blocks.blogs.videos.data);

  const { data: newestData, isLoading: isLoadingNewest } = useQuery({
    queryKey: ['newest'],
    queryFn: () => getNewsBlogService({
      params: blogPageData?.blocks.blogs.newest.data.queries as any,
      endPoint: blogPageData?.blocks.blogs.newest.data.endpoint.split('/').filter(Boolean).pop() || 'news' as string
    }),
    enabled: !!blogPageData?.blocks.blogs.newest
      && blogPageData?.blocks.blogs.newest.data.queries.categoryIds?.length > 0,
  });

  const { data: aboutData, isLoading: isLoadingAbout } = useQuery({
    queryKey: ['about'],
    queryFn: () => getNewsBlogService({
      params: blogPageData?.blocks.blogs.about.data.queries as any,
      endPoint: blogPageData?.blocks.blogs.about.data.endpoint.split('/').filter(Boolean).pop() || 'news' as string
    }),
    enabled: !!blogPageData?.blocks.blogs.about
      && blogPageData?.blocks.blogs.about.data.queries.categoryIds?.length > 0,
  });

  const { data: blogSections, isLoading: isLoadingBlogSections } = useBlogSections(blogPageData);

  const { featured, others } = getFeaturedAndOthers(newestData);

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    infinity: true,
    arrows: false,
    responsive: [
      {
        breakpoint: 1200,
        settings: { slidesToShow: 2 },
      },
      {
        breakpoint: 768,
        settings: { slidesToShow: 1 },
      },
    ],
  };

  return (
    <div className="p-blog">
      <Section>
        {isError ? <NotFoundBlog /> : (
          <div className="p-blog_grid">
            <main className="p-blog_main">
              {isLoadingNewest ? <Loading /> : featured && (
                <div className="p-blog_newsest">
                  <NewsCard
                    img={
                      featured?.img || background
                    }
                    title={featured?.title || ''}
                    description={featured?.description || ''}
                    dateTime={dayjs(featured?.dateTime || '2025/07/01').toISOString()}
                    link={`news/${featured?.slug}` || ''}
                    target={blogPageData?.blocks.blogs.newest.data.viewMore.target as ITarget}
                  />
                </div>
              )}
              {aboutData && aboutData?.length > 0 && (
                <BlogCarousel showArrows={aboutData && aboutData?.length > 2} title="Tin tức về Ask2Auto">
                  {isLoadingAbout ? <Loading />
                    : mapNewsResponseToCards((aboutData) || []).map((item, i) => (
                      <div key={`about-${i.toString()}-${item.slug}`} className="p-blog_section_card">
                        <NewsCard
                          img={item.img}
                          title={item.title}
                          dateTime={item.dateTime}
                          link={`news/${item.slug}`}
                          target={blogPageData?.blocks.blogs.about.data.viewMore.target as ITarget}
                        />
                      </div>
                    ))}
                </BlogCarousel>
              )}

              {isLoadingBlogSections ? <Loading />
                : blogSections.length > 0 && blogSections.map((section, index) => (
                  section && section?.items.length > 0 && (
                    <BlogCarousel
                      key={`section-${index.toString()}-${section?.title}`}
                      showArrows={section && section.items?.length > 2}
                      title={section?.title || 'Tin tức về Ask2Auto'}
                    >
                      {isLoadingAbout ? <Loading />
                        : ((section?.items) || []).map((item, i) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <div key={i} className="p-blog_section_card">
                            <NewsCard
                              img={item.blogData?.thumbnail?.path || background}
                              title={item.blogData?.title || ''}
                              dateTime={item.blogData?.createdAt}
                              link={`blog/${item.blogData?.slug}`}
                              // eslint-disable-next-line max-len
                              target={(blogPageData?.blocks?.blogs?.blogs?.data?.[0] as any)?.posts?.data?.viewMore?.target as ITarget}
                            />
                          </div>
                        ))}
                    </BlogCarousel>
                  )
                ))}

              { /* Social Media */}
              {featuredVideo && featuredVideo.link && (
                <div className="p-blog_section">
                  <div className="p-blog_section_header">
                    <Typography.Text extendClasses="fs-24x30" fontweight="600">
                      Video
                    </Typography.Text>
                  </div>

                  <div
                    onClick={() => window.open(featuredVideo?.link || '', '_blank')}
                    className="p-blog_section_itemImg"
                  >
                    <NativePlayer
                      src={featuredVideo?.link || ''}
                      isYoutube
                    />
                  </div>
                </div>
              )}

              {/* Youtube Media */}
              {featuredVideo && featuredVideo.link && (
                <div className="p-blog_section">
                  <div className="p-blog_section_header">
                    <div onClick={() => window.open('https://www.youtube.com/@EVOLASKLY', '_blank')} className="p-blog_section_brand">
                      <Button variant="text" extendClasses="p-blog_section_buttonLogo fButton fs-16x24">
                        <Icon iconName="askly" size="24" />
                      </Button>
                      <div className="p-blog_section_brandInfo">
                        <Typography.Text extendClasses="fs-20x24 p-blog_section_brandName" fontweight="600">
                          Ask2Auto
                        </Typography.Text>
                        <div className="p-blog_section_youtubeWrap">
                          <div className="p-blog_section_iconPadding p-blog_section_youtubeLogo p-blog-flex">
                            <Icon className="p-blog-mr-10" iconName="youtubeLogo" size="24" />
                            <Typography.Text fontweight="600" extendClasses="fs-16x24 p-blog_section_youtubeText">
                              Youtube
                            </Typography.Text>
                          </div>
                          <Typography.Text extendClasses="fs-16x24 p-blog_section_iconPadding p-blog_section_youtubeCount">
                            999+
                          </Typography.Text>
                        </div>
                      </div>
                    </div>
                    {sliderVideos && sliderVideos?.length > 4 && (
                      <div className="p-blog_section_action">
                        <Button onClick={() => sliderRef.current?.slickPrev()} variant="text" extendClasses="p-blog_section_button fButton fs-16x24">
                          <Icon iconName="previous" size="20" />
                        </Button>
                        <Button onClick={() => sliderRef.current?.slickNext()} variant="text" extendClasses="p-blog_section_button fButton fs-16x24">
                          <Icon iconName="next" size="20" />
                        </Button>
                      </div>
                    )}
                  </div>

                  <Slider className="p-blog_section_item" ref={sliderRef} {...settings}>
                    {sliderVideos.map((item, index) => (
                      <div key={`video-${index.toString()}-${item.link}`} className="p-blog_section_card">
                        <NewsCard
                          img={getYouTubeThumbnail(item.link)}
                          title={item.name || ''}
                          small
                          maxLengthTitle={42}
                          link={item.link || ''}
                        />
                        <div className="p-blog_section_cardPlayOverlay" />
                        <div className="p-blog_section_cardPlayButton">
                          <Icon iconName="playWhite" size="24" />
                        </div>
                      </div>
                    ))}
                  </Slider>
                </div>
              )}
            </main>

            <div className="p-blog_sidebar">
              {others && others.length > 0 && (
                <>
                  <div className="p-blog_sidebar_box">
                    <div className="p-blog_sidebar_header">
                      <Icon iconName="newsPaperOrange" size="30" />
                      <Typography.Text extendClasses="fs-24x30" fontweight="600">
                        Bài viết mới nhất
                      </Typography.Text>
                    </div>
                  </div>

                  <div className="p-blog_cardWrap">
                    {others.map((item) => (
                      <NewsCard
                        key={item.slug}
                        img={item.img || background}
                        title={item.title || 'Untitled'}
                        dateTime={dayjs(item.dateTime).toISOString()}
                        link={`news/${item.slug}` || 'not-found'}
                        target={blogPageData?.blocks.blogs.newest.data.viewMore.target as ITarget}
                      />
                    ))}
                  </div>
                </>
              )}

              <div className={`${others && others.length > 0 ? 'p-blog_cardWrap' : ''}`}>
                <Page
                  href="https://www.facebook.com/evollabs/"
                  tabs="timeline,events,messages"
                  width={350}
                  height={500}
                />
              </div>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
};

export default Blog;
